/**
 * The account's Rules list by folder (§5.27, Claude Design kit 67): where an
 * RFC applies is where its file is, so the list says it — « / · every
 * repository » first, then each folder, its count and + to write an RFC in
 * it. A row dropped on another folder moves the RFC there; a statement's grip
 * dropped on a row moves the statement into that RFC — each kept at once in
 * this browser's draft, a target that cannot take it saying why.
 */
import { useState } from 'react';
import type React from 'react';
import type { RuleRfc } from '@airlock/contracts/rules/document';
import { color, font } from '../../../theme/tokens';
import { byFolder } from './rules-draft';

type Mark = 'new' | 'edited' | 'deleted' | null;
type Carried = { kind: 'rfc'; rfc: number } | { kind: 'statement'; rfc: number; slug: string };

const MIME = 'application/x-airlock-rule';

function carriedOf(event: React.DragEvent): Carried | null {
  try {
    const raw = JSON.parse(event.dataTransfer.getData(MIME)) as Carried;
    return raw && (raw.kind === 'rfc' || raw.kind === 'statement') && Number.isInteger(raw.rfc) ? raw : null;
  } catch {
    return null;
  }
}

export function RulesFolders({
  listed,
  canWrite,
  origin,
  selected,
  count,
  onSelect,
  onNew,
  onMoveRfc,
  onMoveStatement,
  empty,
  Row
}: {
  listed: { rfc: RuleRfc; mark: Mark }[];
  /** Whether rows and statements can be carried, and + offered. */
  canWrite: boolean;
  origin: string;
  selected: number | null;
  count: (rfc: RuleRfc) => number | null;
  onSelect: (rfc: number) => void;
  onNew: (repos: string[]) => void;
  onMoveRfc: (rfc: number, repos: string[]) => void;
  /** A statement dropped on an RFC — the sentence when it cannot land there. */
  onMoveStatement: (from: number, slug: string, to: number) => string | null;
  empty: string | null;
  Row: (props: { rfc: RuleRfc; origin: string; mark: Mark; on: boolean; count: number | null; onSelect: () => void }) => React.JSX.Element;
}): React.JSX.Element {
  const [over, setOver] = useState<string | null>(null);
  const [refused, setRefused] = useState<{ rfc: number; text: string } | null>(null);
  // Data can only be read on drop; while dragging, any carried rule lights a target.
  const takes = (event: React.DragEvent): boolean => canWrite && event.dataTransfer.types.includes(MIME);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', border: `1px solid ${color.line}`, borderRadius: 9, overflow: 'hidden' }}>
      {empty && <p style={{ margin: 0, padding: 14, fontSize: 13, color: color.inkFaint }}>{empty}</p>}
      {byFolder(listed).map((group) => {
        const key = `f:${group.name}`;
        return (
          <div
            key={group.name}
            role="group"
            aria-label={group.name === '/' ? 'Top of the rules repository' : `Folder ${group.name}`}
            onDragOver={(event) => {
              if (!takes(event)) return;
              event.preventDefault();
              if (over !== key) setOver(key);
            }}
            onDragLeave={() => over === key && setOver(null)}
            onDrop={(event) => {
              const carried = carriedOf(event);
              setOver(null);
              if (!carried || carried.kind !== 'rfc') return;
              event.preventDefault();
              onMoveRfc(carried.rfc, group.repos);
            }}
            style={{ display: 'flex', flexDirection: 'column', background: over === key ? color.noteTealBg : color.surface, boxShadow: over === key ? `inset 2px 0 0 ${color.accent}` : 'none' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: `1px solid ${color.hairSoft}`, background: color.surfaceAlt }}>
              <span style={{ flex: 1, minWidth: 0, fontFamily: font.mono, fontSize: 12, fontWeight: 600, color: color.ink, overflowWrap: 'anywhere' }}>
                {group.name}
                {group.name === '/' && <span style={{ fontWeight: 400, color: color.inkFaint }}> · every repository</span>}
              </span>
              <span style={{ fontFamily: font.mono, fontSize: 11, color: color.inkFaint }}>
                {group.items.length} RFC{group.items.length === 1 ? '' : 's'}
              </span>
              {canWrite && (
                <button
                  type="button"
                  title={group.name === '/' ? 'New RFC at the top' : `New RFC in ${group.name}`}
                  aria-label={group.name === '/' ? 'New RFC at the top' : `New RFC in ${group.name}`}
                  onClick={() => onNew(group.repos)}
                  style={{ border: 0, background: 'none', padding: '0 4px', fontSize: 15, lineHeight: 1, color: color.inkMuted, cursor: 'pointer' }}
                >
                  +
                </button>
              )}
            </div>
            {group.items.map(({ rfc, mark }) => {
              const rowKey = `r:${rfc.rfc}`;
              return (
                <div
                  key={rfc.rfc}
                  draggable={canWrite && mark !== 'deleted'}
                  onDragStart={(event) => {
                    event.dataTransfer.setData(MIME, JSON.stringify({ kind: 'rfc', rfc: rfc.rfc }));
                    event.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(event) => {
                    if (!takes(event)) return;
                    event.preventDefault();
                    if (over !== rowKey) setOver(rowKey);
                  }}
                  onDragLeave={() => over === rowKey && setOver(null)}
                  onDrop={(event) => {
                    const carried = carriedOf(event);
                    if (!carried || carried.kind !== 'statement') return;
                    event.preventDefault();
                    event.stopPropagation();
                    setOver(null);
                    const why = onMoveStatement(carried.rfc, carried.slug, rfc.rfc);
                    setRefused(why ? { rfc: rfc.rfc, text: why } : null);
                  }}
                  style={{ boxShadow: over === rowKey ? `inset 0 0 0 1px ${color.accent}` : 'none', cursor: canWrite ? 'grab' : 'default' }}
                >
                  <Row rfc={rfc} origin={origin} mark={mark} on={selected === rfc.rfc} count={count(rfc)} onSelect={() => onSelect(rfc.rfc)} />
                  {refused?.rfc === rfc.rfc && (
                    <p role="alert" style={{ margin: 0, padding: '6px 12px', fontSize: 12, color: color.badText, background: color.badBg }}>
                      {refused.text}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
